/**
 * Telzio Connect Modal — handles username/password + MFA challenge flow.
 *
 * Two-step UX:
 *  1. Enter username/password → POST /api/telzio/connect
 *  2. If `mfa_required` → show MFA input → POST /api/telzio/connect with mfa_code
 */
import React, { useState } from 'react';
import { Phone, X, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const TelzioConnectModal = ({ open, onClose, onConnected }) => {
  const [step, setStep] = useState('credentials'); // credentials | mfa
  const [busy, setBusy] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  if (!open) return null;

  const reset = () => {
    setStep('credentials');
    setUsername('');
    setPassword('');
    setMfaCode('');
    setBusy(false);
  };

  const submit = async () => {
    if (step === 'credentials' && (!username || !password)) {
      toast.error('Username and password are required');
      return;
    }
    if (step === 'mfa' && mfaCode.length < 6) {
      toast.error('Enter your 6-digit MFA code');
      return;
    }
    setBusy(true);
    try {
      const body = { username, password };
      if (step === 'mfa') body.mfa_code = mfaCode.replace(/\s/g, '');
      const res = await authFetch(`${API}/api/telzio/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail?.message || data?.detail || 'Connection failed');
      }
      if (data.mfa_required) {
        setStep('mfa');
        setBusy(false);
        toast.info('Enter the 6-digit code from your authenticator app or SMS');
        return;
      }
      toast.success(`Connected: ${data.first_name || ''} ${data.last_name || ''}`.trim() || 'Telzio connected');
      reset();
      onConnected?.(data);
      onClose();
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="telzio-connect-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-5 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <Phone size={18} className="text-brand" />
            <h3 className="text-base font-semibold text-text-primary">Connect Telzio</h3>
          </div>
          <button onClick={() => { reset(); onClose(); }} className="text-text-muted hover:text-text-primary" data-testid="telzio-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {step === 'credentials' && (
            <>
              <p className="text-xs text-text-tertiary">
                Sign in with your Telzio username and password. Multi-factor authentication will be requested if enabled on your account.
              </p>
              <div>
                <Label className="text-xs">Telzio username</Label>
                <Input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="e.g. smeyler"
                  autoComplete="username"
                  disabled={busy}
                  data-testid="telzio-username"
                />
              </div>
              <div>
                <Label className="text-xs">Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  disabled={busy}
                  onKeyDown={e => e.key === 'Enter' && submit()}
                  data-testid="telzio-password"
                />
              </div>
            </>
          )}

          {step === 'mfa' && (
            <>
              <div className="flex items-start gap-2 bg-brand/5 border border-brand/20 rounded-lg p-3">
                <ShieldCheck size={18} className="text-brand flex-shrink-0 mt-0.5" />
                <p className="text-xs text-text-secondary">
                  Enter the 6-digit code from your authenticator app or SMS to finish connecting <b>{username}</b>.
                </p>
              </div>
              <div>
                <Label className="text-xs">MFA code</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9 ]*"
                  maxLength={7}
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value)}
                  placeholder="123 456"
                  autoComplete="one-time-code"
                  disabled={busy}
                  onKeyDown={e => e.key === 'Enter' && submit()}
                  data-testid="telzio-mfa-code"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-border-subtle">
          <Button variant="ghost" size="sm" onClick={() => { reset(); onClose(); }} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="telzio-submit" className="bg-brand hover:bg-brand-hover">
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
            {step === 'mfa' ? 'Verify code' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TelzioConnectModal;
